/**
 * Parsing API client (Sprint 5).
 *
 * Shapes mirror backend/src/modules/parsing/routes/parsing-routes.ts.
 * All endpoints go through apiRequest which attaches the auth cookie
 * and throws on non-2xx with the server's error envelope.
 */

import { apiRequest } from './client';
import type {
  LoanFileParsingView,
  ReviewQueueItemDTO,
  Paginated,
  ClassificationDTO,
  ExtractionDTO,
} from '@loanaudit/shared';

export interface ReviewQueueQuery {
  page: number;
  pageSize: number;
  itemType?: 'classification' | 'extraction';
  status?: 'pending' | 'approved' | 'corrected' | 'rejected' | 'not_required';
  loanFileId?: string;
  assignedTo?: string;
}

export const parsingApi = {
  getLoanFileView: (loanFileId: string) =>
    apiRequest<LoanFileParsingView>(`/api/parsing/loan-files/${loanFileId}`),

  retrigger: (
    loanFileId: string,
    body: { stage?: 'ocr' | 'classify' | 'extract' | 'all'; reason?: string },
  ) =>
    apiRequest<{ queuedDocuments: number }>(
      `/api/parsing/loan-files/${loanFileId}/retrigger`,
      { method: 'POST', body: { stage: body.stage ?? 'all', reason: body.reason } },
    ),

  overrideClassification: (
    documentId: string,
    body: { newType: string; reason: string },
  ) =>
    apiRequest<{ updated: true }>(
      `/api/parsing/documents/${documentId}/classification/override`,
      { method: 'POST', body },
    ),

  listReviewQueue: (q: ReviewQueueQuery) =>
    apiRequest<Paginated<ReviewQueueItemDTO>>('/api/parsing/review-queue', {
      query: q as unknown as Record<string, string | number | boolean | undefined>,
    }),

  getReviewItem: (reviewId: string) =>
    apiRequest<{
      review: ReviewQueueItemDTO;
      source: ClassificationDTO | ExtractionDTO | null;
    }>(`/api/parsing/review-queue/${reviewId}`),

  assignReviewItem: (reviewId: string, assigneeUserId: string | null) =>
    apiRequest<ReviewQueueItemDTO>(`/api/parsing/review-queue/${reviewId}/assign`, {
      method: 'POST',
      body: { assigneeUserId },
    }),

  resolveReviewItem: (
    reviewId: string,
    body: {
      action: 'approve' | 'correct' | 'reject';
      correctedType?: string;
      correctedFields?: Record<string, unknown>;
      notes?: string;
    },
  ) =>
    apiRequest<ReviewQueueItemDTO>(`/api/parsing/review-queue/${reviewId}/resolve`, {
      method: 'POST',
      body,
    }),
};
